import { FormatOptions } from '@doss/common-all'

/**
 * Shared date-format preset/pattern logic for DATE and DATE_TIME formatting. Kept as a plain util
 * (rather than inline in the DateFormatFields component) so both the table-column and Formv2 hosts
 * — and any future consumer that needs to map a preset to a dayjs pattern — draw from one source.
 */
export const DatePreset = {
  DEFAULT: 'DEFAULT',
  ISO: 'ISO',
  US: 'US',
  EU: 'EU',
  LONG: 'LONG',
  CUSTOM: 'CUSTOM',
} as const

export type DatePreset = (typeof DatePreset)[keyof typeof DatePreset]

export type BuiltInDatePreset = Exclude<DatePreset, typeof DatePreset.CUSTOM>

export const DATE_PATTERNS: Record<BuiltInDatePreset, string> = {
  [DatePreset.DEFAULT]: '',
  [DatePreset.ISO]: 'YYYY-MM-DD',
  [DatePreset.US]: 'MM/DD/YYYY',
  [DatePreset.EU]: 'DD/MM/YYYY',
  [DatePreset.LONG]: 'MMMM D, YYYY',
}

export const DATE_TIME_PATTERNS: Record<BuiltInDatePreset, string> = {
  [DatePreset.DEFAULT]: '',
  [DatePreset.ISO]: 'YYYY-MM-DD HH:mm',
  [DatePreset.US]: 'MM/DD/YYYY hh:mm A',
  [DatePreset.EU]: 'DD/MM/YYYY HH:mm',
  [DatePreset.LONG]: 'MMMM D, YYYY hh:mm A',
}

/** Reads the dayjs `pattern` carried on the format options (present when the preset is DATE_CUSTOM). */
export const getDatePattern = (options: FormatOptions | null): string | undefined => {
  if (options && 'pattern' in options) {
    return options.pattern
  }

  return undefined
}

/**
 * Maps a stored pattern back to its built-in preset for the dropdown, falling back to CUSTOM when the
 * pattern doesn't match any built-in (and undefined when there is no pattern at all).
 */
export const findSelectedPreset = (
  pattern: string | undefined,
  isDateTime: boolean,
): DatePreset | undefined => {
  if (pattern === undefined) {
    return undefined
  }

  const patterns = isDateTime ? DATE_TIME_PATTERNS : DATE_PATTERNS
  const preset = (Object.keys(patterns) as BuiltInDatePreset[]).find(
    (value) => patterns[value] === pattern,
  )

  return preset ?? DatePreset.CUSTOM
}
